import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import mermaid from 'mermaid';

/**
 * A Mermaid diagram on the light artifact page: full width, Sitra-ish colours,
 * no preview cap. The chat's MermaidBlock initialises mermaid dark and small;
 * artifact pages have no chat, so they may configure the library for light.
 * The chart source is AI output built from workshop content: strict sanitizer.
 */
let initialized = false;
function ensureInit() {
  if (initialized) return;
  initialized = true;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'neutral',
    themeVariables: {
      fontFamily: 'RationalText, Inter, sans-serif',
      primaryColor: '#dce8f3', primaryTextColor: '#292929', primaryBorderColor: '#3b78b0',
      lineColor: '#636363', secondaryColor: '#e8f3ea', tertiaryColor: '#fff6cc',
      background: '#ffffff', mainBkg: '#dce8f3', nodeBorder: '#3b78b0', clusterBkg: '#f2f2f2',
      titleColor: '#292929', edgeLabelBackground: '#ffffff',
    },
    flowchart: { curve: 'basis', padding: 12, htmlLabels: true },
    fontSize: 14,
    securityLevel: 'strict',
    suppressErrorRendering: true,
  });
}
let queue = Promise.resolve();

const Box = styled.div`
  margin: 12px 0; padding: 12px; background: #fff; border: 1px solid var(--color-grey-4, #d9d9d9); border-radius: 8px; overflow-x: auto;
  svg { max-width: 100%; height: auto; display: block; margin: 0 auto; }
`;
const Err = styled.pre` font-size: 12px; color: var(--color-error); white-space: pre-wrap; `;

export default function LightMermaid({ chart }) {
  const ref = useRef(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!chart?.trim()) return undefined;
    ensureInit();
    let cancelled = false;
    const id = `art-mermaid-${Math.random().toString(36).slice(2, 10)}`;
    queue = queue.then(async () => {
      if (cancelled) return;
      try {
        const { svg } = await mermaid.render(id, chart.trim());
        if (!cancelled && ref.current) ref.current.innerHTML = svg;
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      }
    });
    return () => { cancelled = true; };
  }, [chart]);
  if (error) return <Err>{error}</Err>;
  return <Box ref={ref} />;
}
